import { useCurrentUser } from 'api';
import { Header, Omnisearch, UserMenu } from 'features';
import { safiLogo } from 'features/brand';
import { useTranslation } from 'i18n';
import { addError } from 'monitor';
import { Link } from 'react-router-dom';
import { Box, Button, IconButton, Skeleton, Stack, useBreakpoint } from 'ui';
import { Menu } from 'ui/icons';

export const HEADER_HEIGHT = 8;

const HeaderLogo = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        display: 'flex',
        ...(!isMobile && {
          left: '50%',
          position: 'absolute',
          transform: 'translateX(-50%)',
        }),
        ...(isMobile && {
          left: theme.spacing(HEADER_HEIGHT),
          position: 'absolute',
        }),
      })}
    >
      <Box
        component="img"
        src={safiLogo}
        alt="Safi logo"
        sx={{
          height: '31px',
        }}
      />
    </Box>
  );
};

export const HeaderLinks = () => {
  const { t } = useTranslation('orders');
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        sx={{
          width: 100,
          height: 35,
        }}
      />
    );
  }

  if (user?.is_superuser) {
    return (
      <Stack
        direction="row"
        sx={{
          gap: 1,
        }}
      >
        <Button variant="secondary" component={Link} to="/ground-control/">
          {t('internalHeader.links.groundControl')}
        </Button>
        <Button variant="secondary" component="a" href="/post-deal/dealhub/">
          {t('internalHeader.links.dealHub')}
        </Button>
        <Button variant="secondary" component="a" href="/quality-control/">
          {t('internalHeader.links.qualityControl')}
        </Button>
      </Stack>
    );
  }

  if (user?.is_buyer) {
    return (
      <Button variant="secondary" component="a" href="/shipments/">
        {t('internalHeader.links.shipments')}
      </Button>
    );
  }

  if (user?.is_seller) {
    return (
      <Stack
        direction="row"
        sx={{
          gap: 1,
        }}
      >
        <Button variant="secondary" component="a" href="/deals/">
          {t('internalHeader.links.deals')}
        </Button>
        <Button variant="secondary" component="a" href="/collections/">
          {t('internalHeader.links.collections')}
        </Button>
      </Stack>
    );
  }
  addError(Error('Unable to determine user type when rendering HeaderLinks'));
  return null;
};

export const InternalHeader = ({ onMenuClick }: { onMenuClick: () => void }) => {
  const { t } = useTranslation('orders');
  const { isMobile } = useBreakpoint();
  return (
    <Header>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton aria-label={t('internalHeader.aria.openSidebar')} onClick={onMenuClick}>
          <Menu />
        </IconButton>
        {!isMobile && <HeaderLinks />}
      </Stack>
      <HeaderLogo isMobile={isMobile} />
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Omnisearch />

        <UserMenu />
      </Stack>
    </Header>
  );
};
